import React, { useEffect, useState } from 'react';
import { m } from 'framer-motion';
// @mui
import { Grid, Box, Button, Typography, Chip } from '@mui/material';
// components
import Image from 'src/components/Image';
import { varFade } from 'src/components/animate';
import HubspotContactForm from 'src/components/forms/HubspotContactForm';
import { contactFormConfig } from 'src/components/forms/hubspot-config';
// hooks
import useSubscriptions from 'src/hooks/useSubscriptions';
// @types
import { PlanName, PlanType, Subscription, UserPlan } from 'src/@types/feature';
// utils
import { getPlanPrice, getPriceSavingInYear, staticPlanData } from 'src/utils/subscriptions';
import { formatPriceTextOnly } from 'src/utils/priceFormatter';
// styles
import { StyledPlanItem } from './styles';

interface PlanItemProps {
  plan: UserPlan;
  matchPlan?: Subscription;
  currentSubscriptions: Subscription[];
  idx: number;
  selectedBtn: PlanType;
}

const PlanItem: React.FC<PlanItemProps> = ({
  plan,
  matchPlan,
  currentSubscriptions,
  idx,
  selectedBtn,
}) => {
  const [isOpenContactModal, setIsOpenContactModal] = useState(false);

  const {
    subscriptionsAreLoading,
    setPriceSavingForYear,
    onSubscribeButtonClick,
    onUpdateToAnnualButtonClick,
    planCurrency,
    isCurrentPlanEnterprise,
    isStripeSubscription,
    isPremiumUser
  } = useSubscriptions();
  const isAnnualPlansOpen = selectedBtn === PlanType.ANNUAL;
  const isPlanEnterprise = plan?.planLevel === PlanName.ENTERPRISE;

  const currentPlanStarterAnnual = currentSubscriptions?.find(el => el?.recurringInterval === PlanType.ANNUAL && el?.planLevel === PlanName.STARTER && !el?.trial);
  const currentPlanAdvancedAnnual = currentSubscriptions?.find(el => el?.recurringInterval === PlanType.ANNUAL && el?.planLevel === PlanName.ADVANCED && !el?.trial);
  const currentPlanStarterMonthly = currentSubscriptions?.find(el => el?.recurringInterval === PlanType.MONTHLY && el?.planLevel === PlanName.STARTER && !el?.trial);
  const monthlyPlan = currentSubscriptions?.find(el => el?.recurringInterval !== PlanType.ANNUAL && el?.planLevel === plan?.planLevel);
  const planAdvanced = plan?.planLevel === PlanName.ADVANCED;
  const planStarter = plan?.planLevel === PlanName.STARTER;

  const planPrice = getPlanPrice(selectedBtn, planCurrency, plan);

  const UpgradeToAnnualBtn = () => (
    <Button
      onClick={() => onUpdateToAnnualButtonClick(currentPlanStarterMonthly ? 20 : 30, planCurrency, PlanType.ANNUAL)}
      variant="contained"
      className='branded'
    >
      Upgrade to annual
    </Button>
  )

  const UpgradeToAdvancedBtn = () => (
    <Button
      onClick={() => onUpdateToAnnualButtonClick(30, planCurrency, isAnnualPlansOpen ? PlanType.ANNUAL : PlanType.MONTHLY)}
      variant="contained"
      className='branded'
    >
      Upgrade to Advanced
    </Button>
  )

  const SubscribeBth = () => (
    <Button
      onClick={() => planPrice && onSubscribeButtonClick(planPrice?.id, plan?.planLevel)}
      variant="outlined"
      color='primary'
    >
      Subscribe
    </Button>
  )

  const ContactUsBtn = () => (
    <Button
      onClick={() => setIsOpenContactModal(true)}
      variant={currentPlanAdvancedAnnual ? "contained" : "outlined"}
      color="primary"
    >
      Contact us
    </Button>
  )

  const renderCtaButton = () => {
    if (!subscriptionsAreLoading && isStripeSubscription) {
      return <ContactUsBtn />
    }

    if (!subscriptionsAreLoading) {
      if (currentPlanAdvancedAnnual && planAdvanced) {
        return <Typography variant='button' color="primary">You’re on Advanced</Typography>
      } else if (currentPlanStarterAnnual && planStarter) {
        return <Typography variant='button' color="primary">You’re on Starter</Typography>
      } else if (isCurrentPlanEnterprise && isPlanEnterprise) {
        return <Typography variant='button' color="primary">You’re on Enterprise</Typography>
      } else if (currentPlanStarterAnnual && planAdvanced) {
        return <UpgradeToAdvancedBtn />
      } else if (monthlyPlan && !isPlanEnterprise) {
        return <UpgradeToAnnualBtn />
      } else if (!isPlanEnterprise) {
        return <SubscribeBth />
      } else {
        return <ContactUsBtn />
      }
    }
  }

  const formattedPrice = (price: { currency: string, unitAmount: number }, type?: string) => {
    return price && formatPriceTextOnly({
      currency: price?.currency,
      symbol: '',
      value: type === PlanType.ANNUAL ? price?.unitAmount / 12 : price?.unitAmount,
      zeroDecimalCurrency: false,
    });
  }

  const planBgImage = staticPlanData?.find((el) => el?.planLevel === plan?.planLevel)?.planImage;
  const month_priceDescription = selectedBtn === PlanType.MONTHLY && !isPlanEnterprise && 'billed monthly';
  const year_priceDescription = isAnnualPlansOpen && !isPlanEnterprise && planPrice && `${formattedPrice(planPrice)} billed annually`;

  const starterPriceYearSaving = getPriceSavingInYear(PlanName.STARTER, planCurrency, plan);
  const advancedPriceYearSaving = getPriceSavingInYear(PlanName.ADVANCED, planCurrency, plan);
  
  useEffect(() => {
    starterPriceYearSaving && setPriceSavingForYear((prev: any) => ({ ...prev, [PlanName.STARTER]: starterPriceYearSaving }))
    advancedPriceYearSaving && setPriceSavingForYear((prev: any) => ({ ...prev, [PlanName.ADVANCED]: advancedPriceYearSaving }))
  }, [starterPriceYearSaving, advancedPriceYearSaving, setPriceSavingForYear]);

  return (
    <Grid
      item xs={12}
      sm={6}
      md={4}
      component={m.div}
      {...varFade({ durationIn: idx + 0.15 }).inUp}
    >
      <StyledPlanItem className={plan?.planLevel === PlanName.ADVANCED ? 'planHighlight' : ''}>
        <Box sx={{ position: 'relative' }}>
          {plan?.planLevel === PlanName.ADVANCED && <Chip label="Most popular" className="mostPopular" />}
          <Image
            visibleByDefault
            disabledEffect
            ratio="16/9"
            src={planBgImage}
            sx={{
              '& img': {
                objectFit: plan?.planLevel === PlanName.STARTER ? 'fill' : 'cover',
              }
            }}
          />
          {isAnnualPlansOpen && !isPlanEnterprise && 
            <Chip label={plan?.planLevel === PlanName.STARTER ? starterPriceYearSaving : advancedPriceYearSaving} className="priceLabel" />
          }
        </Box>

        <Box sx={{ px: { xs: 2, md: 4 }, py: 2 }}>
          <Box>
            <Typography
              variant="h5"
              gutterBottom
              textAlign="center"
              color={matchPlan && isPremiumUser && !currentPlanAdvancedAnnual && !currentPlanStarterAnnual && !isCurrentPlanEnterprise ? 'primary' : 'initial'}
            >
              {matchPlan && isPremiumUser && !currentPlanAdvancedAnnual && !currentPlanStarterAnnual && !isCurrentPlanEnterprise && `You're on`} {plan?.name}
            </Typography>

            <Typography variant="body2">{plan?.description}</Typography>
          </Box>

          <Box sx={{ textAlign: 'center', my: 4 }}>
            <Box sx={{ mb: 1.5 }}>
              {!isPlanEnterprise && (
                <Typography variant="h6" sx={{ lineHeight: 1 }}>
                  {planPrice && 
                    <>{isAnnualPlansOpen 
                        ? formattedPrice(planPrice, PlanType.ANNUAL) 
                        : formattedPrice(planPrice)
                      }
                    </>
                  }
                  {isAnnualPlansOpen && !isPlanEnterprise && (
                    <Typography variant="caption" sx={{ ml: 1 }}>/mo</Typography>
                  )}
                </Typography>
              )}
              <Typography variant="caption">{month_priceDescription || year_priceDescription}</Typography>
            </Box>

            {renderCtaButton()}
          </Box>

          <Box>
            <Typography variant="tableHeader" gutterBottom>Available Features include</Typography>

            {plan?.features?.map((feature: any, idx: number) => (
              <Typography key={idx} variant="caption" sx={{ display: 'block', lineHeight: 1.8 }}>
                {feature?.name}
              </Typography>
            ))}
          </Box>
        </Box>
      </StyledPlanItem>

      {isOpenContactModal && (
        <HubspotContactForm
          isModalOpen={isOpenContactModal}
          setIsModalOpen={setIsOpenContactModal}
          region={contactFormConfig.region}
          portalId={contactFormConfig.portalId}
          formId={contactFormConfig.formId}
        />
      )}
    </Grid>
  );
};

export default PlanItem;
